import React, {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from "react"
import { useLocation, useNavigate } from "react-router-dom"

import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { getAccessibleTour, getGuideTour } from "../content/tours.ts"
import { useGuideProgress } from "../hooks/use-guide-progress.ts"
import type { GuideTour, GuideTourStep } from "../types/guide-content.types.ts"

interface GuideTourContextValue {
  activeTour: GuideTour | null
  activeStep: GuideTourStep | null
  stepIndex: number
  totalSteps: number
  startTour: (tourId: string) => void
  next: () => void
  back: () => void
  skip: () => void
}

const GuideTourContext = createContext<GuideTourContextValue | null>(null)

export const GuideTourProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { setTourComplete } = useGuideProgress()
  const pages = useAppSelector((state) => state.auth?.permissions?.pages)

  const [activeTour, setActiveTour] = useState<GuideTour | null>(null)
  const [stepIndex, setStepIndex] = useState(0)

  const startTour = useCallback(
    (tourId: string) => {
      const tour = getGuideTour(tourId)
      if (!tour) return
      // Filtered once, here — a step the user has no permission for is
      // simply never part of the tour they get, not something skipped
      // mid-run for a permission reason.
      const accessibleTour = getAccessibleTour(tour, pages)
      if (accessibleTour.steps.length === 0) return
      if (location.pathname !== accessibleTour.route)
        navigate(accessibleTour.route)
      setActiveTour(accessibleTour)
      setStepIndex(0)
    },
    [navigate, location.pathname, pages]
  )

  const stop = useCallback(() => {
    setActiveTour(null)
    setStepIndex(0)
  }, [])

  const next = useCallback(() => {
    if (!activeTour) return
    if (stepIndex >= activeTour.steps.length - 1) {
      setTourComplete(activeTour.id)
      stop()
      return
    }
    setStepIndex((index) => index + 1)
  }, [activeTour, stepIndex, setTourComplete, stop])

  const back = useCallback(() => {
    setStepIndex((index) => Math.max(0, index - 1))
  }, [])

  const value = useMemo<GuideTourContextValue>(
    () => ({
      activeTour,
      activeStep: activeTour?.steps[stepIndex] ?? null,
      stepIndex,
      totalSteps: activeTour?.steps.length ?? 0,
      startTour,
      next,
      back,
      skip: stop
    }),
    [activeTour, stepIndex, startTour, next, back, stop]
  )

  return (
    <GuideTourContext.Provider value={value}>
      {children}
    </GuideTourContext.Provider>
  )
}

// A safe no-op rather than throwing: GuideHelpTrigger is meant to be dropped
// into any page across the app (and rendered standalone in that page's own
// tests), while GuideTourProvider only actually wraps the tree once, in
// CustomLayout. Outside of it, "Take a tour" quietly does nothing instead of
// crashing a host page that never asked to depend on the Guide.
const NOOP_TOUR_CONTEXT: GuideTourContextValue = {
  activeTour: null,
  activeStep: null,
  stepIndex: 0,
  totalSteps: 0,
  startTour: () => {},
  next: () => {},
  back: () => {},
  skip: () => {}
}

export const useGuideTour = (): GuideTourContextValue =>
  useContext(GuideTourContext) ?? NOOP_TOUR_CONTEXT
